import { MappingStorage } from "../neolab-libs/nl-lib2-pagestorage/background-pdf-mapper/MappingStorage";
import { IBrushEnum, IPageSOBP, isSamePageOnlySOBP, makeNPageIdStr, NeoStroke, uuidv4 } from "../neolab-libs/nl-lib3-common";
import { ProjectExamType } from "../repositories/model/support/ProjectExamType";
import { drawPath } from "./DrawCurves";
import { loadBackgroundPdf } from "./loadBackgroundPdf";
import { NeoPDFManager } from "../nl-pdf-wrapper-importer";

export const sobpImgMap = new Map();
export const sobpImgSizeMap = new Map();

const imgLoadedSobpSet = new Set();

const stringToRgb = (str) => {
	if (str.startsWith('#')) {
		const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(str);
		let r = 0,
			g = 0,
			b = 0;
		if (result) {
			r = parseInt(result[1], 16);
			g = parseInt(result[2], 16);
			b = parseInt(result[3], 16);
		}

		// console.log('stringToRgb str 2', r, g, b);
		return { r, g, b };
	} else {
		const values = str.match(/\d+/g);
		// 문자열을 정수로 변환
		const r = parseInt(values[0]);
		const g = parseInt(values[1]);
		const b = parseInt(values[2]);
		return { r, g, b };
	}
};


export const getPageSize = async (sobp: IPageSOBP, numPages: number, pageIndex: number, selectedPdf) => {
	console.log('getPageSize', sobp, pageIndex, selectedPdf);
	const sobpStr = makeNPageIdStr(sobp);

	if (!sobpImgSizeMap.has(sobpStr)) {
		const doc = await loadBackgroundPdf(
			ProjectExamType.DESCRIPTIVE, "ko",
			sobp, selectedPdf, numPages);

		if (!doc) return;

		const page = await doc.pdf.getPage(pageIndex);
		const pageSize = await page.getSize();

		sobpImgSizeMap.set(sobpStr, pageSize);
	}

	return sobpImgSizeMap.get(sobpStr);
};

export const sobpImageConverter = async (args: {
	type: ProjectExamType,
	lang: string,
	sobp: IPageSOBP,
	numPages: number,
	pageIndex: number,
	ndpNcodeRange: { section: number, owner: number, bookCode: number, pageStart: number, pageEnd: number },
	forceUpdate?: boolean,
	dpi?: number,
	supersample?: number,
	maxOutputSize?: number,
	outputFormat?: 'jpeg' | 'png',
	quality?: number,
	wholeStrokes: NeoStroke[]
}) => {
	const { wholeStrokes, type, lang, sobp, numPages, pageIndex, ndpNcodeRange, forceUpdate = false, dpi = 100, supersample = 2, maxOutputSize = 2048, outputFormat = 'jpeg', quality = 0.9 } = args;
	const sobpStr = makeNPageIdStr(sobp);

	if (!imgLoadedSobpSet.has(sobpStr) || forceUpdate) {
		imgLoadedSobpSet.add(sobpStr);

		const doc = await loadBackgroundPdf(type, lang, sobp, ndpNcodeRange, numPages);

		console.log('sobpImageConverter', doc, sobp, ndpNcodeRange, pageIndex);

		if (!doc) return;

		const page = await doc.pdf.getPage(pageIndex);

		const pdfItem = MappingStorage.instance.getAssociatedPdfPage(sobp);
		const homography = pdfItem.pageMapping.h;

		// const strokes = InkStorage.instance.getPageStrokes(sobp);
		const strokes = wholeStrokes.filter((stroke) => isSamePageOnlySOBP(stroke.sobp, sobp));

		console.log('sobpImageConverter page strokes', strokes);

		for (let j = 0; j < strokes.length; j++) {
			const stroke = strokes[j];
			const { status, thickness, brushType, dotArray: da, color } = stroke;

			const rrggbb = stringToRgb(color);

			const { a: a0, b: b0, c: c0, d: d0, e: e0, f: f0, g: g0, h: h0 } = homography;

			// console.log('homography', homography);

			let opacity = 1;
			if (brushType === IBrushEnum.MARKER) {
				opacity = 0.3;
			}

			const pointArray = [];

			for (let k = 0, len_k = da.length; k < len_k; k++) {
				const dot = da[k];
				const nom = g0 * dot.x + h0 * dot.y + 1;
				const px = (a0 * dot.x + b0 * dot.y + c0) / nom;
				const py = (d0 * dot.x + e0 * dot.y + f0) / nom;

				const pdf_xy = { x: px, y: py };

				const f = dot.f;
				pointArray.push({ x: pdf_xy.x, y: pdf_xy.y, f });
			}
			// page.setRotation(degrees(gridaPageObj.rotation));

			let strokeThickness = thickness * 1.3; // * 4
			switch (brushType) {
				case IBrushEnum.MARKER:
					strokeThickness *= 5;
					break;
				default:
					break;
			}

			const pathData = drawPath(pointArray, strokeThickness);

			// console.log("pathData", pathData, rrggbb, opacity);

			await page.addSVGPath({
				svgPath: pathData,
				r: 1.0,
				g: 1.0,
				b: 1.0,
				a: opacity,
				// scale: 1
			});
		}

		const renderDpi = Math.max(30, Math.round(dpi * Math.max(1, supersample)));
		const urlTask = page.renderPageAsPngDataUrl(renderDpi);
		const urlPromise = await urlTask.promise;
		let dataUrl = urlPromise.dataUrl as string;

		if (maxOutputSize && maxOutputSize > 0) {
			dataUrl = await (async () => {
				return new Promise<string>((resolve) => {
					const img = new Image();
					img.onload = () => {
						const srcW = img.naturalWidth;
						const srcH = img.naturalHeight;
						const scale = Math.min(1, maxOutputSize / Math.max(srcW, srcH));
						const dstW = Math.max(1, Math.round(srcW * scale));
						const dstH = Math.max(1, Math.round(srcH * scale));

						const canvas = document.createElement('canvas');
						canvas.width = dstW;
						canvas.height = dstH;
						const ctx = canvas.getContext('2d');
						if (ctx) {
							ctx.imageSmoothingEnabled = true;
							// @ts-ignore
							ctx.imageSmoothingQuality = 'high';
							ctx.fillStyle = '#ffffff';
							ctx.fillRect(0, 0, dstW, dstH);
							ctx.drawImage(img, 0, 0, dstW, dstH);
						}
						const mime = outputFormat === 'png' ? 'image/png' : 'image/jpeg';
						const q = outputFormat === 'png' ? undefined : quality;
						const out = canvas.toDataURL(mime, q as any);
						resolve(out);
					};
					img.src = dataUrl;
				});
			})();
		}

		const pageSize = await page.getSize();

		console.log('converter', sobpStr, pageSize);

		if (pageSize !== undefined) {
			sobpImgSizeMap.set(sobpStr, pageSize);
		}
		sobpImgMap.set(sobpStr, dataUrl);



	}

	// console.log(`converter return`);

	return { url: sobpImgMap.get(sobpStr), pageSize: sobpImgSizeMap.get(sobpStr) };
};

export const createImage = async (args: {
	dpi?: number,
	supersample?: number,
	maxOutputSize?: number,
	outputFormat?: 'jpeg' | 'png',
	quality?: number,
	padding?: number,
	strokes: NeoStroke[]
}) => {
	const {
		strokes,
		dpi = 200,
		supersample = 3,
		maxOutputSize = 2400,
		outputFormat = 'jpeg',
		quality = 0.9,
		padding = 12
	} = args;

	// 1) Prepare homography and transform all points first to compute tight bounding box
	const homography = {
		a: 6.72,
		b: 0,
		c: 0,
		d: 0,
		e: 6.72,
		f: 0,
		g: 0,
		h: 0
	};

	const { a: a0, b: b0, c: c0, d: d0, e: e0, f: f0, g: g0, h: h0 } = homography as any;

	let minX = Number.POSITIVE_INFINITY;
	let minY = Number.POSITIVE_INFINITY;
	let maxX = Number.NEGATIVE_INFINITY;
	let maxY = Number.NEGATIVE_INFINITY;

	const transformedStrokes: { points: { x: number; y: number; f: number }[]; thickness: number; color: string; opacity: number }[] = [];

	for (let j = 0; j < strokes.length; j++) {
		const stroke = strokes[j];
		const { thickness, brushType, dotArray: da, color } = stroke;

		let opacity = 1;
		if (brushType === IBrushEnum.MARKER) {
			opacity = 0.3;
		}

		const points = [] as { x: number; y: number; f: number }[];
		for (let k = 0, len_k = da.length; k < len_k; k++) {
			const dot = da[k];
			const nom = g0 * dot.x + h0 * dot.y + 1;
			const px = (a0 * dot.x + b0 * dot.y + c0) / nom;
			const py = (d0 * dot.x + e0 * dot.y + f0) / nom;
			console.log(`px: ${px}, py: ${py}`);
			const f = dot.f;
			points.push({ x: px, y: py, f });

			if (px < minX) minX = px;
			if (py < minY) minY = py;
			if (px > maxX) maxX = px;
			if (py > maxY) maxY = py;
		}

		let strokeThickness = thickness * 2.0;
		switch (brushType) {
			case IBrushEnum.MARKER:
				strokeThickness *= 5;
				break;
			default:
				break;
		}

		transformedStrokes.push({ points, thickness: strokeThickness, color, opacity });
	}

	// Handle empty strokes
	if (!Number.isFinite(minX) || !Number.isFinite(minY) || !Number.isFinite(maxX) || !Number.isFinite(maxY)) {
		const size = 64;
		const canvas = document.createElement('canvas');
		canvas.width = size;
		canvas.height = size;
		const ctx = canvas.getContext('2d');
		if (ctx) {
			ctx.imageSmoothingEnabled = true;
			// @ts-ignore
			ctx.imageSmoothingQuality = 'high';
			ctx.fillStyle = '#ffffff';
			ctx.fillRect(0, 0, size, size);
		}
		const mime = outputFormat === 'png' ? 'image/png' : 'image/jpeg';
		const q = outputFormat === 'png' ? undefined : quality;
		const dataUrl = canvas.toDataURL(mime, q as any);
		const pageSize = { width: size, height: size } as any;
		return { url: dataUrl, pageSize };
	}

	const width = Math.max(1, Math.ceil(maxX - minX + padding * 2));
	const height = Math.max(1, Math.ceil(maxY - minY + padding * 2));

	// 2) Create a tightly sized PDF page and rebase points
	const NPM = await NeoPDFManager.getInstance();
	const pdf = await NPM.createDocument({ uniqueKey: uuidv4(), name: 'A4-Blank' });
	await pdf.addBlankPage(width, height, 0);
	const pageIndex = 0;
	const page = await pdf.getPage(pageIndex);

	for (let i = 0; i < transformedStrokes.length; i++) {
		const { points, thickness, color, opacity } = transformedStrokes[i];
		const rrggbb = stringToRgb(color);

		const shifted = points.map(p => ({ x: p.x - minX + padding, y: p.y - minY + padding, f: p.f }));
		const pathData = drawPath(shifted, thickness);

		await page.addSVGPath({
			svgPath: pathData,
			r: rrggbb.r,
			g: rrggbb.g,
			b: rrggbb.b,
			a: opacity,
		});
	}

	// 3) Render at higher effective DPI using supersampling
	const renderDpi = Math.max(72, Math.round(dpi * Math.max(1, supersample)));
	const urlTask = page.renderPageAsPngDataUrl(renderDpi);
	const urlPromise = await urlTask.promise;
	let dataUrl = urlPromise.dataUrl as string;

	// 4) Downscale to target max dimension and convert to JPEG/PNG for better OCR performance
	if (maxOutputSize && maxOutputSize > 0) {
		dataUrl = await (async () => {
			return new Promise<string>((resolve) => {
				const img = new Image();
				img.onload = () => {
					const srcW = img.naturalWidth;
					const srcH = img.naturalHeight;
					const scale = Math.min(1, maxOutputSize / Math.max(srcW, srcH));
					const dstW = Math.max(1, Math.round(srcW * scale));
					const dstH = Math.max(1, Math.round(srcH * scale));

					const canvas = document.createElement('canvas');
					canvas.width = dstW;
					canvas.height = dstH;
					const ctx = canvas.getContext('2d');
					if (ctx) {
						ctx.imageSmoothingEnabled = true;
						// @ts-ignore
						ctx.imageSmoothingQuality = 'high';
						// Fill white background to avoid transparency hurting OCR
						ctx.fillStyle = '#ffffff';
						ctx.fillRect(0, 0, dstW, dstH);
						ctx.drawImage(img, 0, 0, dstW, dstH);
					}
					const mime = outputFormat === 'png' ? 'image/png' : 'image/jpeg';
					const q = outputFormat === 'png' ? undefined : quality;
					const out = canvas.toDataURL(mime, q as any);
					resolve(out);
				};
				img.src = dataUrl;
			});
		})();
	}

	const pageSize = { width, height } as any;
	console.log('createImage result pageSize', pageSize);

	return { url: dataUrl, pageSize };
};

export const createImages = async (args: {
    dpi?: number,
    supersample?: number,
    maxOutputSize?: number,
    outputFormat?: 'jpeg' | 'png',
    quality?: number,
    padding?: number,
    codeToStrokes: Record<string, NeoStroke[]>
}) => {
    const {
        dpi = 200,
        supersample = 3,
        maxOutputSize = 2400,
        outputFormat = 'jpeg',
        quality = 0.9,
        padding = 10,
        codeToStrokes
    } = args;

    const results: Record<string, { url: string; pageSize: { width: number; height: number } }> = {};
    const codesInOrder = Object.keys(codeToStrokes);
    for (let i = 0; i < codesInOrder.length; i++) {
        const code = codesInOrder[i];
        const strokes = codeToStrokes[code] ?? [];
        const { url, pageSize } = await createImage({ dpi, supersample, maxOutputSize, outputFormat, quality, padding, strokes });
        results[code] = { url, pageSize };
    }
    return results;
};

export default loadBackgroundPdf;
